import { useState, useEffect } from 'react'
import { PlusIcon, TrashIcon, PencilIcon, CheckIcon } from '@heroicons/react/24/outline'
import { Button } from '@/components/common/Button'
import { Input } from '@/components/common/Input'
import { Switch } from '@/components/common/Switch'
import { useTableColumnsQuery, useAddColumnMutation, useModifyColumnMutation, useDropColumnMutation } from '@/hooks/useDatabase'
import { toast } from 'react-hot-toast'

interface TableEditorProps {
  connectionId: string
  database: string
  schema?: string
  tableName: string
  databaseType?: string
  onClose?: () => void
}

interface EditingColumn {
  name: string
  dataType: string
  nullable: boolean
  defaultValue?: string
  comment?: string
  autoIncrement?: boolean
  isNew?: boolean
  originalName?: string
  isEditing?: boolean
  isDeleted?: boolean
  maxLength?: number
  precision?: number
  scale?: number
}

const DATA_TYPES: Record<string, string[]> = {
  MySQL: [
    'INT', 'BIGINT', 'TINYINT', 'SMALLINT', 'MEDIUMINT',
    'DECIMAL(10,2)', 'NUMERIC(10,2)', 'FLOAT', 'DOUBLE',
    'VARCHAR(255)', 'CHAR(10)', 'TEXT', 'MEDIUMTEXT', 'LONGTEXT', 'TINYTEXT',
    'DATE', 'TIME', 'DATETIME', 'TIMESTAMP', 'YEAR',
    'BOOLEAN', 'BOOL',
    'BLOB', 'MEDIUMBLOB', 'LONGBLOB', 'TINYBLOB',
    'JSON', 'ENUM', 'SET'
  ],
  PostgreSQL: [
    'INTEGER', 'BIGINT', 'SMALLINT', 'INT2', 'INT4', 'INT8',
    'DECIMAL(10,2)', 'NUMERIC(10,2)', 'REAL', 'DOUBLE PRECISION', 'FLOAT4', 'FLOAT8',
    'VARCHAR(255)', 'CHAR(10)', 'TEXT', 'CHARACTER VARYING(255)', 'CHARACTER(10)',
    'DATE', 'TIME', 'TIMESTAMP', 'TIMESTAMPTZ', 'INTERVAL',
    'BOOLEAN', 'BOOL',
    'UUID', 'JSON', 'JSONB', 'XML',
    'BYTEA', 'ARRAY', 'CIDR', 'INET', 'MACADDR'
  ]
}

export function TableEditor({ connectionId, database, schema, tableName, databaseType = 'MySQL', onClose }: TableEditorProps) {
  const [columns, setColumns] = useState<EditingColumn[]>([])
  const [hasChanges, setHasChanges] = useState(false)
  const [editingColumnIndex, setEditingColumnIndex] = useState<number | null>(null)
  const [originalColumns, setOriginalColumns] = useState<EditingColumn[]>([])
  
  const { data: existingColumns, isLoading, refetch } = useTableColumnsQuery(
    connectionId,
    database,
    tableName,
    schema
  )
  
  const addColumnMutation = useAddColumnMutation(connectionId, database, tableName, schema)
  const modifyColumnMutation = useModifyColumnMutation(connectionId, database, tableName, schema)
  const dropColumnMutation = useDropColumnMutation(connectionId, database, tableName, schema)
  
  useEffect(() => {
    if (existingColumns && Array.isArray(existingColumns)) {
      const formattedColumns = existingColumns
        .filter((col: unknown): col is Record<string, unknown> => {
          // Filter out columns without names (check both name and column_name)
          if (col && typeof col === 'object' && !Array.isArray(col)) {
            const c = col as Record<string, unknown>
            return Boolean(c.name || c.column_name)
          }
          return false
        })
        .map((col) => {
        // Map API response properties to expected properties
        const columnName = (col.name) || (col.column_name!) || ''
        const columnType = (col.type) || (col.data_type!) || ''
        const columnNullable = col.nullable !== undefined ? col.nullable : col.is_nullable
        const columnDefault = (col.defaultValue) || (typeof col.column_default === 'string' ? col.column_default : null) || undefined
        const columnComment = (col.comment) || (typeof col.column_comment === 'string' ? col.column_comment : null) || ''
        const columnAutoIncrement = col.autoIncrement !== undefined ? col.autoIncrement : col.is_auto_increment
        const maxLength = (col.maxLength) || (typeof col.character_maximum_length === 'number' ? col.character_maximum_length : null) || undefined
        const precision = (col.precision) || (typeof col.numeric_precision === 'number' ? col.numeric_precision : null) || undefined
        const scale = (col.scale) || (typeof col.numeric_scale === 'number' ? col.numeric_scale : null) || undefined
        
        // Build the complete data type string including length/precision
        let dataType = columnType
        
        // For character varying and similar types, append the length
        if (maxLength && maxLength > 0) {
          if (dataType.includes('character varying')) {
            dataType = `character varying(${maxLength})`
          } else if (dataType === 'varchar') {
            dataType = `varchar(${maxLength})`
          } else if (dataType === 'char') {
            dataType = `char(${maxLength})`
          }
        }
        
        // For numeric types, append precision and scale
        if (precision && (dataType === 'numeric' || dataType === 'decimal')) {
          dataType = scale ? `${dataType}(${precision},${scale})` : `${dataType}(${precision})`
        }
        
        return {
          name: columnName,
          dataType,
          nullable: Boolean(columnNullable),
          defaultValue: columnDefault,
          comment: columnComment || '',
          autoIncrement: Boolean(columnAutoIncrement),
          originalName: columnName,
          isNew: false,
          isEditing: false,
          isDeleted: false
        } as EditingColumn
      })
      
      setColumns(formattedColumns)
      setOriginalColumns(formattedColumns)
    }
  }, [existingColumns])
  
  const addNewColumn = () => {
    const newColumn: EditingColumn = {
      name: `new_column_${columns.length + 1}`,
      dataType: 'VARCHAR(255)',
      nullable: true,
      defaultValue: '',
      comment: '',
      autoIncrement: false,
      isNew: true,
      isEditing: true
    }
    setColumns([...columns, newColumn])
    setEditingColumnIndex(columns.length)
    setHasChanges(true)
  }
  
  const updateColumn = (index: number, updates: Partial<EditingColumn>) => {
    const newColumns = Array.from(columns)
    newColumns[index] = { ...newColumns[index], ...updates }
    setColumns(newColumns)
    
    // Check if we actually have changes by comparing with original state
    const hasAnyChanges = newColumns.some((col) => {
      if (col.isNew || col.isDeleted) return true
      
      const originalCol = originalColumns.find(c => c.originalName === col.originalName)
      if (!originalCol) return false
      
      return col.name !== originalCol.name ||
             col.dataType !== originalCol.dataType ||
             col.nullable !== originalCol.nullable ||
             col.defaultValue !== originalCol.defaultValue ||
             col.comment !== originalCol.comment ||
             col.autoIncrement !== originalCol.autoIncrement
    })
    
    setHasChanges(hasAnyChanges)
  }
  
  const deleteColumn = (index: number) => {
    const newColumns = Array.from(columns)
    if (newColumns[index].isNew) {
      // If it's a new column, just remove it
      newColumns.splice(index, 1)
      // Adjust editing index if needed
      if (editingColumnIndex === index) {
        setEditingColumnIndex(null)
      } else if (editingColumnIndex !== null && editingColumnIndex > index) {
        setEditingColumnIndex(editingColumnIndex - 1)
      }
    } else {
      // Toggle deletion for existing columns
      if (newColumns[index].isDeleted) {
        // Restore column
        newColumns[index] = { ...newColumns[index], isDeleted: false }
      } else {
        // Mark for deletion
        newColumns[index] = { ...newColumns[index], isDeleted: true, isEditing: false }
        if (editingColumnIndex === index) {
          setEditingColumnIndex(null)
        }
      }
    }
    setColumns(newColumns)
    
    // Check if we actually have changes
    const hasAnyChanges = newColumns.some((col) => {
      if (col.isNew || col.isDeleted) return true
      
      const originalCol = originalColumns.find(c => c.originalName === col.originalName)
      if (!originalCol) return false
      
      return col.name !== originalCol.name ||
             col.dataType !== originalCol.dataType ||
             col.nullable !== originalCol.nullable ||
             col.defaultValue !== originalCol.defaultValue ||
             col.comment !== originalCol.comment ||
             col.autoIncrement !== originalCol.autoIncrement
    })
    
    setHasChanges(hasAnyChanges)
  }
  
  const toggleEditColumn = (index: number) => {
    if (editingColumnIndex === index) {
      // Save edit
      setEditingColumnIndex(null)
      const newColumns = Array.from(columns)
      newColumns[index].isEditing = false
      setColumns(newColumns)
    } else {
      // Cancel any existing edit first
      if (editingColumnIndex !== null) {
        const newColumns = Array.from(columns)
        // If the column being edited is new and has invalid data, don't allow switching
        if (newColumns[editingColumnIndex].isNew) {
          const col = newColumns[editingColumnIndex]
          if (!col.name || col.name.trim() === '' || !col.dataType || col.dataType.trim() === '') {
            toast.error('Please complete the new column before editing another')
            return
          }
        }
        newColumns[editingColumnIndex].isEditing = false
        setColumns(newColumns)
      }
      // Start editing new column
      setEditingColumnIndex(index)
      const newColumns = Array.from(columns)
      newColumns[index].isEditing = true
      setColumns(newColumns)
    }
  }
  
  const validateChanges = (): string | null => {
    const columnNames = columns.filter(col => !col.isDeleted).map(col => col.name)
    const uniqueNames = new Set(columnNames)
    
    if (columnNames.length !== uniqueNames.size) {
      return 'Column names must be unique'
    }
    
    for (const col of columns.filter(c => !c.isDeleted)) {
      if (!col.name || col.name.trim() === '') {
        return 'Column name cannot be empty'
      }
      if (!col.dataType || col.dataType.trim() === '') {
        return 'Data type cannot be empty'
      }
      // Validate column name format
      if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(col.name)) {
        return `Invalid column name: ${col.name}. Column names must start with a letter or underscore and contain only letters, numbers, and underscores.`
      }
    }
    
    return null
  }
  
  const saveChanges = async () => {
    const validationError = validateChanges()
    if (validationError) {
      toast.error(validationError)
      return
    }
    
    // Collect all operations to perform
    const operations: {
      operation: string;
      column: {
        name: string;
        type: string;
        nullable?: boolean;
        default_value?: string | null;
        auto_increment?: boolean;
        primary_key?: boolean;
      };
    }[] = []
    
    for (const col of columns) {
      if (col.isNew && !col.isDeleted) {
        // New column to add
        operations.push({
          operation: 'add',
          column: {
            name: col.name,
            type: col.dataType,
            nullable: col.nullable,
            default_value: col.defaultValue || null,
            auto_increment: col.autoIncrement
          }
        })
      } else if (col.isDeleted && !col.isNew) {
        // Existing column to drop
        operations.push({
          operation: 'drop',
          column: {
            name: col.originalName!,
            type: '' // Required field, but not used for drop operation
          }
        })
      } else if (!col.isNew && !col.isDeleted) {
        // Find the original column from originalColumns (which has the same formatted dataType)
        const originalCol = originalColumns.find(c => c.originalName === col.originalName)
        
        if (originalCol) {
          // Check if column was actually modified
          const hasChanges = 
            col.name !== originalCol.name ||
            col.dataType !== originalCol.dataType ||
            col.nullable !== originalCol.nullable ||
            col.defaultValue !== originalCol.defaultValue ||
            col.comment !== originalCol.comment ||
            col.autoIncrement !== originalCol.autoIncrement
          
          if (hasChanges) {
            operations.push({
              operation: 'modify',
              column: {
                name: col.originalName || col.name,
                type: col.dataType,
                nullable: col.nullable,
                default_value: col.defaultValue || null,
                auto_increment: col.autoIncrement
              }
            })
          }
        }
      }
    }
    
    // Only proceed if there are actual changes
    if (operations.length === 0) {
      toast('No changes to save')
      return
    }
    
    try {
      // Execute operations one by one
      for (const op of operations) {
        if (op.operation === 'add') {
          await addColumnMutation.mutateAsync({
            name: op.column.name,
            type: op.column.type,
            nullable: op.column.nullable,
            default_value: op.column.default_value,
            auto_increment: op.column.auto_increment
          })
        } else if (op.operation === 'modify') {
          await modifyColumnMutation.mutateAsync({
            columnName: op.column.name,
            newColumn: {
              name: op.column.name,
              type: op.column.type,
              nullable: op.column.nullable
            }
          })
        } else if (op.operation === 'drop') {
          await dropColumnMutation.mutateAsync(op.column.name)
        }
      }
      
      toast.success('Table structure updated successfully')
      setHasChanges(false)
      void refetch()
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update table structure'
      toast.error(errorMessage)
    }
  }
  
  const cancelChanges = () => {
    // Restore from originalColumns which has the properly formatted data types
    setColumns(originalColumns.map(col => ({ ...col })))
    setHasChanges(false)
    setEditingColumnIndex(null)
  }
  
  if (isLoading) {
    return (
      <div className="h-full flex flex-col p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-6"></div>
          <div className="space-y-3">
            <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded"></div>
          </div>
        </div>
      </div>
    )
  }
  
  return (
    <div className="h-full flex flex-col p-6">
      <div className="mb-6 flex items-center justify-between flex-shrink-0">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Edit Table: {schema ? `${schema}.${tableName}` : tableName}
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Manage table columns and structure
          </p>
        </div>
        <Button
          onClick={onClose}
          variant="secondary"
          size="sm"
        >
          Back to Table View
        </Button>
      </div>
      
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg flex-1 flex flex-col min-h-0">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Columns</h3>
            <Button
              onClick={addNewColumn}
              size="sm"
              className="flex items-center gap-2"
            >
              <PlusIcon className="w-4 h-4" />
              Add Column
            </Button>
          </div>
        </div>
        
        <div className="overflow-auto flex-1">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900 sticky top-0 z-10 shadow-sm">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Data Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Nullable
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Default
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Comment
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider sticky right-0 bg-gray-50 dark:bg-gray-900">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {columns.map((column, index) => (
                <tr 
                  key={index} 
                  className={`${column.isDeleted ? 'opacity-50 bg-red-50 dark:bg-red-900/20' : ''} ${column.isNew ? 'bg-green-50 dark:bg-green-900/20' : ''}`}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    {column.isEditing ? (
                      <Input
                        value={column.name}
                        onChange={(e) => { updateColumn(index, { name: e.target.value }) }}
                        size="sm"
                        disabled={column.isDeleted}
                      />
                    ) : (
                      <span className={`text-sm font-medium ${column.isDeleted ? 'line-through' : ''} text-gray-900 dark:text-white`}>
                        {column.name}
                        {column.isNew && <span className="ml-2 text-xs text-green-600 dark:text-green-400">(new)</span>}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {column.isEditing ? (
                      <Input
                        value={column.dataType || ''}
                        onChange={(e) => { updateColumn(index, { dataType: e.target.value }) }}
                        size="sm"
                        placeholder="Enter data type"
                        disabled={column.isDeleted}
                        list={`datatype-list-${index}`}
                      />
                    ) : (
                      <span className={`text-sm ${column.isDeleted ? 'line-through' : ''} text-gray-500 dark:text-gray-400 font-mono`}>
                        {column.dataType}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {column.isEditing ? (
                      <Switch
                        checked={column.nullable || false}
                        onChange={(checked) => { updateColumn(index, { nullable: checked }) }}
                        disabled={column.isDeleted}
                      />
                    ) : (
                      <span className={`text-sm ${column.isDeleted ? 'line-through' : ''} text-gray-500 dark:text-gray-400`}>
                        {column.nullable ? 'Yes' : 'No'}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {column.isEditing ? (
                      <Input
                        value={column.defaultValue || ''}
                        onChange={(e) => { updateColumn(index, { defaultValue: e.target.value }) }}
                        size="sm"
                        placeholder="NULL"
                        disabled={column.isDeleted}
                      />
                    ) : (
                      <span className={`text-sm ${column.isDeleted ? 'line-through' : ''} text-gray-500 dark:text-gray-400`}>
                        {column.defaultValue || 'NULL'}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {column.isEditing ? (
                      <Input
                        value={column.comment || ''}
                        onChange={(e) => { updateColumn(index, { comment: e.target.value }) }}
                        size="sm"
                        disabled={column.isDeleted}
                      />
                    ) : (
                      <span className={`text-sm ${column.isDeleted ? 'line-through' : ''} text-gray-500 dark:text-gray-400`}>
                        {column.comment || '-'}
                      </span>
                    )}
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap sticky right-0 ${column.isDeleted ? 'bg-red-50 dark:bg-red-900/20' : column.isNew ? 'bg-green-50 dark:bg-green-900/20' : 'bg-white dark:bg-gray-800'}`}>
                    <div className="flex items-center gap-2">
                      {!column.isDeleted && (
                        <button type="button"
                          onClick={() => { toggleEditColumn(index) }}
                          className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                          title={column.isEditing ? "Save" : "Edit"}
                        >
                          {column.isEditing ? (
                            <CheckIcon className="w-4 h-4" />
                          ) : (
                            <PencilIcon className="w-4 h-4" />
                          )}
                        </button>
                      )}
                      <button type="button"
                        onClick={() => { deleteColumn(index) }}
                        className={column.isDeleted ? "text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300" : "text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"}
                        title={column.isDeleted ? "Restore" : "Delete"}
                      >
                        {column.isDeleted ? (
                          <CheckIcon className="w-4 h-4" />
                        ) : (
                          <TrashIcon className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {/* Datalists for data type suggestions */}
          {columns.map((_, index) => (
            <datalist key={index} id={`datatype-list-${index}`}>
              {DATA_TYPES[databaseType].map(type => (
                <option key={type} value={type} />
              ))}
            </datalist>
          ))}
        </div>
        
        {hasChanges && (
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 flex-shrink-0">
            <div className="flex items-center justify-end gap-3">
              <Button
                onClick={cancelChanges}
                variant="secondary"
                size="sm"
              >
                Cancel
              </Button>
              <Button
                onClick={() => void saveChanges()}
                variant="primary"
                size="sm"
                loading={addColumnMutation.isPending || modifyColumnMutation.isPending || dropColumnMutation.isPending}
              >
                Save Changes
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}